import { AdAccountDTO } from 'api-models'
import { useEffect, useReducer, useState } from 'react'
import { toast } from 'react-toastify'

import { GButton, GConfirm } from 'components/basic-blocks'
import GDrawer from 'components/basic-blocks/g-drawer/g-drawer'

import { useStores } from 'hooks'
import { LevelType } from 'layout/navbar/breadcrumbs/types'
import { Rules } from 'services/user/rules'
import { ChatService } from 'services/chat'
import { truncateString } from '../../../../../../../../utils/text'
import { useTrackPostHogEvent } from '../../../../../../../../utils/posthog'
import { PostHogEvent, PostHogPath } from '../../../../../../../../utils/posthog-events'
import { Actions } from './components/actions'
import { AdditionalCriteria } from './components/additional-criteria'
import { RuleInfo } from './components/rule-info'
import { Trigger } from './components/trigger'
import { resetAction, ruleReducer } from './reducer'
import { RuleFormTypeProps } from './types'
import { defaultRuleFormValue, prepareRuleParams, validateRule } from './utils'

export const RuleForm = ({
	children,
	rule,
	refetch,
	level,
	targetId,
	targetName,
	platform,
	isEditing = false,
	handleDeleteRule
}: RuleFormTypeProps) => {
	const { assetStore } = useStores()
	const { asset } = assetStore
	const trackPostHogEvent = useTrackPostHogEvent()

	const [isDialogOpen, setIsDialogOpen] = useState(false)
	const [loading, setLoading] = useState(false)
	const [errors, setErrors] = useState<Record<string, string> | null>(null)

	const initialState = rule || defaultRuleFormValue(level)
	const [ruleForm, dispatch] = useReducer(ruleReducer, { ...initialState })

	const toggleDialog = () => {
		setIsDialogOpen((prev) => !prev)
		if (!isDialogOpen) dispatch(resetAction(initialState))
	}

	const clearError = (key: string) => {
		setErrors((prev) => {
			const copy = { ...prev }
			delete copy[key]
			return copy
		})
	}

	const handleSubmit = async () => {
		try {
			setLoading(true)

			const { isValid, errors: validationErrors } = validateRule(
				ruleForm,
				level
			)

			if (!isValid) {
				setErrors(validationErrors)
				const elementName = Object.keys(validationErrors)[0]
				const element =
					document.getElementsByName(elementName)[0] ??
					document.querySelector<HTMLElement>(
						`[data-rule-field="${elementName}"] button`
					)
				element?.focus()

				return
			}

			const preparedRuleParams = prepareRuleParams(ruleForm, level)

			if (isEditing) {
				await Rules.updateCustomRule({
					accountId: asset?.account_id,
					ruleId: rule?.id,
					payload: { rule_data: preparedRuleParams }
				})
			} else {
				await Rules.createCustomRule({
					accountId: asset?.account_id,
					payload: {
						asset_id: asset?.id,
						ad_account_id: level === 'ad-account' ? targetId : null,
						campaign_id: level === 'campaign' ? targetId : null,
						rule_data: preparedRuleParams
					}
				})
							trackPostHogEvent(PostHogEvent.ADD_CUSTOM_RULE, PostHogPath.AI_AND_CUSTOMRULES, null)
			}

			refetch()
			toggleDialog()
			toast.success(`Rule ${isEditing ? 'edited' : 'created'} successfully`)
		} catch (e) {
			toast.error(`Error while ${isEditing ? 'editing' : 'creating'} rule`)
		} finally {
			setLoading(false)
		}
	}

	useEffect(() => {
		if (isDialogOpen) ChatService.hide()
		if (!isDialogOpen) ChatService.show()
	}, [isDialogOpen])

	return (
		<>
			{children && children(() => toggleDialog())}
			<GDrawer
				isOpen={isDialogOpen}
				handleClose={toggleDialog}
				label={`${isEditing ? 'Edit custom rule' : 'Create custom rule'}`}
				subLabel={
					<HeaderText
						isEditing={isEditing}
						name={targetName}
						level={level}
						platform={platform}
					/>
				}
				backgroundColor="bg-gray-100"
			>
				<div className="w-[700px] flex flex-col mt-10">
					<div className="bg-white mx-8 pb-6 rounded-xl">
						<main className="flex flex-col gap-4 pl-8 pr-6 mt-4">
							<RuleInfo
								rule={ruleForm}
								platform={platform}
								targetName={targetName}
								level={level}
								errors={errors}
								dispatch={dispatch}
								clearError={clearError}
							/>
							<Trigger
								rule={ruleForm}
								level={level}
								errors={errors}
								dispatch={dispatch}
								clearError={clearError}
								targetId={targetId}
							/>
							<AdditionalCriteria
								focus={ruleForm?.trigger.focus}
								modifiers={ruleForm.modifiers}
								errors={errors}
								threshold={ruleForm.threshold}
								dispatch={dispatch}
								clearError={clearError}
							/>
							<Actions
								rule={ruleForm}
								errors={errors}
								dispatch={dispatch}
								clearError={clearError}
							/>
						</main>
						<footer className="flex items-center gap-2 justify-between mt-4 pt-4 px-6">
							<div className="flex">
								{isEditing && handleDeleteRule && (
									<GConfirm
										type="danger"
										title={`Delete rule ${truncateString(
											rule?.name || '',
											15
										)}`}
										description={`Are you sure you want to remove custom rule ${truncateString(
											rule?.name || '',
											15
										)}? This action cannot be undone.`}
										onConfirm={() => handleDeleteRule(rule?.id)}
										closeModalOnConfirm={true}
									>
										<GButton
											color="danger"
											variant="text"
											size="sm"
											className="font-medium text-t-red outline-0  w-full rounded-full  text-base hover:bg-transparent shadow-none border-t-radio-gray"
										>
											Delete rule
										</GButton>
									</GConfirm>
								)}
							</div>
							<GButton
								label="Save rule"
								variant="contained"
								color="yellow"
								labelClassName="font-medium text-base"
								className="h-[38px] w-full rounded-full disabled:!shadow-none !shadow-s-yellow"
								onClick={handleSubmit}
								loading={loading}
							/>
						</footer>
					</div>
				</div>
			</GDrawer>
		</>
	)
}

export const HeaderText = (props: {
	isEditing: boolean
	name?: string | null
	level: LevelType
	platform?: AdAccountDTO['platform']
}) => {
	const { isEditing, name, level, platform } = props

	const text = [
		`You are ${isEditing ? 'editing' : 'creating'} a custom protection rule for`
	]

	if (platform === 'GADS') text.push('Google ads')
	if (platform === 'FADS') text.push('Meta ads')
	if (platform === 'BADS') text.push('Microsoft ads')

	if (level === 'website') text.push('website')
	if (level === 'ad-account') text.push('account')
	if (level === 'campaign') text.push('campaign')

	return (
		<header className="flex items-center">
			{text.join(' ')}
			<strong className="ml-1 max-w-[200px] text-t-checkbox-unactive truncate block">{name}</strong>
		</header>
	)
}
